import { z } from 'zod';
import { VENUES } from '../../ontology/calendar.ts';
import { DAY_PARTS } from '../../ontology/day-part.ts';
import { NOTIFY_KINDS, NOTIFY_VOICES } from '../../ontology/objects/notification.ts';
import { idSchema, TRADE_TYPES } from '../../ontology/primitives.ts';
import { STANDOUT_KINDS } from '../../ontology/standout.ts';
import { lineSchema } from './copy.ts';
import { indicatedOpenSchema, verdictSchema } from './indication.ts';
import { newsStorySchema } from './news.ts';
import { wireNotificationSchema } from './notification.ts';
import { day, instant } from './primitives.ts';
import { HEALTHS, slotSchema } from './systems.ts';

const count = z.number().int().nonnegative();

/** In the digest's order, which Home keeps: a part says a subset of these, never another order. */
export const BRIEF_KINDS = [
  'no-fallback',
  'gapped',
  'indicated',
  'log-in',
  'expiring',
  'carries',
  'held-into',
  'stock',
  'drafts',
  'desk-session',
  'ready',
  'reads',
  'filings',
  'orders',
  'heat',
  'calendar',
  'events',
  'news',
  'participants',
  'holiday',
  'banked',
  'indicated-quiet',
  'indicated-note',
  'own-week',
  'desk-week',
  'systems',
  'chores',
] as const;

export type BriefKind = (typeof BRIEF_KINDS)[number];

const NEED_KINDS = [
  'offer',
  'exit',
  'follow-an-analyst',
  'follow-requests',
  'roll',
  'system',
  'unguarded',
  'access',
  'applications',
  'standing',
] as const;

const briefLineSchema = z.object({
  kind: z.enum([...BRIEF_KINDS, ...NEED_KINDS]),
  /** Stable across reads, so a row keeps its place and a new one can be told from an old. */
  key: z.string(),
  voice: z.enum(NOTIFY_VOICES),
  headline: z.string(),
  said: lineSchema,
  href: z.string(),
  /** The button's words where the line is a page of its own; null where it is only a link. */
  action: z.string().nullable(),
  by: instant.nullable(),
  /** About the reader's own money or decisions, not the desk's or the market's. */
  mine: z.boolean(),
  /** The note kinds it stands for: an unread note of one of these shows once, as this line. */
  covers: z.array(z.enum(NOTIFY_KINDS)),
  /** The money it puts at risk, in R, which orders it within its voice. */
  r: z.number().nullable(),
});

export type BriefLine = z.infer<typeof briefLineSchema>;

const urgency = {
  key: z.string(),
  voice: z.enum(NOTIFY_VOICES),
  r: z.number().nullable(),
  by: instant.nullable(),
};

const needSchema = z.discriminatedUnion('from', [
  z.object({
    from: z.literal('line'),
    line: briefLineSchema,
    notes: z.array(idSchema),
    ...urgency,
  }),
  z.object({ from: z.literal('note'), note: wireNotificationSchema, ...urgency }),
]);

export type Need = z.infer<typeof needSchema>;

const needsSchema = z.object({
  /** Loudest first: voice, then R, then deadline. */
  rows: z.array(needSchema),
  /** The state lines could not be read: never drawn as nothing needing the reader. */
  unread: z.boolean(),
  standing: briefLineSchema.nullable(),
});

export type HomeNeeds = z.infer<typeof needsSchema>;

const AHEAD_KINDS = [
  'open',
  'close',
  'event',
  'release',
  'expiry',
  'tender',
  'stock',
  'holiday',
  'note',
] as const;

const aheadItemSchema = z.object({
  /** Its kind, its day and what it is of, as `open:2026-10-05:NFO`, so a row keeps its place. */
  id: z.string(),
  /** Null where the source dates it to the day and not the hour. */
  at: instant.nullable(),
  day,
  kind: z.enum(AHEAD_KINDS),
  said: lineSchema,
  key: z.boolean(),
  touches: z.array(z.string()),
  /** Those of `touches` the reader holds; the rest they only follow calls on. */
  held: z.array(z.string()),
  /** Rupees a 1% move is on what the reader holds of it, net of side, options left out. */
  stake: z.number().nullable(),
  href: z.string(),
});

export type AheadItem = z.infer<typeof aheadItemSchema>;

const beatSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('slot'), place: instant, slot: slotSchema }),
  z.object({ kind: z.literal('ahead'), place: instant, item: aheadItemSchema }),
  z.object({ kind: z.literal('bell'), place: instant, said: z.string() }),
  z.object({
    kind: z.literal('notes'),
    /** The newest note's. */
    place: instant,
    head: z.object({ said: z.string(), href: z.string() }).nullable(),
    /** Back-to-back notes about one recommendation, oldest first. */
    notes: z.array(wireNotificationSchema).min(1),
  }),
]);

export type Beat = z.infer<typeof beatSchema>;

const todaySchema = z.object({
  allDay: z.array(aheadItemSchema),
  beats: z.array(beatSchema),
  week: z.array(z.object({ day, items: z.array(aheadItemSchema) })),
});

export type HomeToday = z.infer<typeof todaySchema>;

const watchSchema = z.object({
  key: z.string(),
  href: z.string(),
  held: z.boolean(),
  tradeType: z.enum(TRADE_TYPES),
  analyst: z.string().nullable(),
  /** A held leg on another contract than the call's levels. */
  on: z.string().nullable(),
  /** Null for a call on an option, whose premium is not the underlying's move. */
  verdict: verdictSchema.nullable(),
  levels: z.object({ entryPrice: z.number(), stoplossPrice: z.number(), targetPrice: z.number() }),
  /** A held leg's fallback stop, where it is on the indicated contract's own scale. */
  fallback: z.number().nullable(),
  /** That fallback, where the open is indicated past it and it would trigger there. */
  firing: z.number().nullable(),
});

export type Watch = z.infer<typeof watchSchema>;

const gapSchema = z.object({
  venue: z.enum(VENUES),
  opens: instant.nullable(),
  blocks: z.array(
    z.object({
      indication: indicatedOpenSchema,
      held: z.array(watchSchema),
      followed: z.array(watchSchema),
    }),
  ),
  rest: z.array(indicatedOpenSchema),
});

export type GapVenue = z.infer<typeof gapSchema>;

const openedSchema = z.object({
  tradingsymbol: z.string(),
  commodity: z.string(),
  /** The 08:30 reading, the one members were told. */
  indicated: z.number(),
  /** MCX's open; null until the night is scored at 09:02. */
  actual: z.number().nullable(),
});

export type HomeOpened = z.infer<typeof openedSchema>;

const weekSchema = z.object({
  /** This week's trading days: the ledger's net, null where none was read, and R banked on the day's closes. */
  days: z.array(z.object({ day, net: z.number().nullable(), rClosed: z.number() })),
  closed: z.array(z.object({ analystId: idSchema, bookId: idSchema, r: z.number(), count: count })),
  /** On a day off, what a member's holdings made this week, money put in or out taken off. */
  worth: z.number().nullable(),
  filed: count,
  closedCount: count,
});

export type HomeWeek = z.infer<typeof weekSchema>;

const PATH_MARKS = ['fill', 'exit'] as const;

/** Today's P/L at each quarter-hour close, with the fills and exits that moved it. */
const pathSchema = z.object({
  points: z.array(z.object({ at: instant, rupees: z.number() })),
  marks: z.array(z.object({ at: instant, kind: z.enum(PATH_MARKS), positionId: idSchema })),
  /** Each priced leg's day as of the read, closed or still held. */
  legs: z.array(
    z.object({
      positionId: idSchema,
      tradingsymbol: z.string(),
      underlying: z.string(),
      tradeType: z.enum(TRADE_TYPES),
      quantity: z.number(),
      rupees: z.number(),
    }),
  ),
  basis: z.string(),
});

export type HomePath = z.infer<typeof pathSchema>;

const TICKER_KINDS = ['commodity', 'rate', 'index', 'world'] as const;

/** The tape's rows of markets, in their order: NSE's indices, MCX's commodities, the US's, the rest. */
export const TAPE_GROUPS = ['india', 'mcx', 'us', 'world'] as const;

const range = z.object({ high: z.number(), low: z.number() });

/** One market on the ticker; its later prices ride the socket's quotes. */
const tickerMarketSchema = z.object({
  kind: z.enum(TICKER_KINDS),
  /** The row of the tape it is drawn in. */
  group: z.enum(TAPE_GROUPS),
  /** What the tape calls it, the same across the front month's roll: GOLD, NIFTY 50, US 10Y. */
  name: z.string(),
  token: z.number().int().positive(),
  /** A commodity's month: the contract a filing or autopilot would take. */
  contract: z.string().nullable(),
  /** Whose session it keeps on the desk's calendar; null for one that keeps none of them. */
  venue: z.enum(VENUES).nullable(),
  /** The close its move is measured from: the session's before the one `last` is in. */
  close: z.number().nullable(),
  /** The stream's last price as read with Home, and when. */
  last: z.object({ price: z.number(), at: instant }).nullable(),
  /** The range of the session `last` is in. */
  day: range.nullable(),
  /** The closes of the year before that session. */
  year: range.nullable(),
  /** While its venue is shut and the open is indicated. */
  indicated: indicatedOpenSchema.nullable(),
  /** What its own record says of the move and the level, by rule: the level first. */
  standouts: z.array(z.object({ kind: z.enum(STANDOUT_KINDS), said: z.string() })),
  /** The reader holds a position on it, or follows an open call on it. */
  tie: z.enum(['held', 'followed']).nullable(),
});

export type TickerMarket = z.infer<typeof tickerMarketSchema>;

const tickerHoldingSchema = z.object({
  tradingsymbol: z.string(),
  quantity: z.number(),
  lastPrice: z.number(),
  /** Rupees off the previous close; null where the day's move is not yet known. */
  day: z.number().nullable(),
  dayFraction: z.number().nullable(),
  /** Rupees off what was paid. */
  gain: z.number(),
});

export type TickerHolding = z.infer<typeof tickerHoldingSchema>;

const holdingsSchema = z.discriminatedUnion('read', [
  z.object({
    read: z.literal(true),
    at: instant,
    value: z.number(),
    /** What they cost. */
    invested: z.number(),
    /** Today's move across them, while NSE trades today or after its close; null before. */
    day: z.number().nullable(),
    dayFraction: z.number().nullable(),
    /** The day's largest move in rupees first, or the largest holding where there is no day yet. */
    rows: z.array(tickerHoldingSchema),
  }),
  z.object({ read: z.literal(false), reason: z.enum(['logged-out', 'failed']) }),
]);

export type TickerHoldings = z.infer<typeof holdingsSchema>;

const tickerSchema = z.object({
  /** What the reader holds or follows first, then the commodities, the yield, NSE and the world. */
  markets: z.array(tickerMarketSchema),
  /** A member's own at their broker; null for the desk and for a member with no broker linked. */
  holdings: holdingsSchema.nullable(),
});

export type Ticker = z.infer<typeof tickerSchema>;

const holdersSchema = z.object({ recommendationId: idSchema, members: count });

/** Each figure is null where its own read failed, drawn as not read; the rest still come. */
const deskSchema = z.object({
  verdict: z.object({ worst: z.enum(HEALTHS), said: z.string(), count }),
  ready: z
    .object({
      placing: count,
      live: count,
      notLive: z.array(z.object({ memberId: idSchema, name: z.string() })),
    })
    .nullable(),
  filings: z.object({ today: count, channel: count, alert: count, add: count }).nullable(),
  orders: z.object({ sent: count, filled: count, refused: count }).nullable(),
  heat: z.object({ rupees: z.number(), trades: count, members: count }).nullable(),
  unguarded: count.nullable(),
  drafts: count.nullable(),
  requests: z.object({ access: count, analysts: count, follows: count }).nullable(),
  slots: z.array(slotSchema),
  holders: z.array(holdersSchema).nullable(),
});

export type HomeDesk = z.infer<typeof deskSchema>;

const analystSchema = z.object({
  drafts: count,
  requests: count,
  holders: z.array(holdersSchema),
});

export type HomeAnalyst = z.infer<typeof analystSchema>;

/** What moved the reader's things, then the day's biggest stories that are not theirs. */
const homeNewsSchema = z.object({
  yours: z.array(newsStorySchema),
  markets: z.array(newsStorySchema),
  /** The newest story on each name the reader is in or the ticker draws, a mover's line. */
  said: z.array(z.object({ name: z.string(), story: newsStorySchema })),
});

export type HomeNews = z.infer<typeof homeNewsSchema>;

export const homeSchema = z.object({
  at: instant,
  part: z.enum(DAY_PARTS),
  needs: needsSchema,
  brief: z.array(briefLineSchema),
  /** Null where it could not be read. */
  news: homeNewsSchema.nullable(),
  /** Null means it could not be read, never "nothing scheduled". */
  ahead: z.array(aheadItemSchema).nullable(),
  today: todaySchema,
  gap: z.array(gapSchema),
  opened: z.array(openedSchema),
  /** After the close and on a day off. */
  week: weekSchema.nullable(),
  /** A member's, from the open on. */
  path: pathSchema.nullable(),
  /** A linked member's funds at each one's latest NAV: `on` the latest, `behind` how many a day before it. */
  funds: z.object({ rupees: z.number(), on: day, behind: count }).nullable(),
  ticker: tickerSchema,
  desk: deskSchema.nullable(),
  analyst: analystSchema.nullable(),
});

export type Home = z.infer<typeof homeSchema>;
